<mat-card class="addon-card">
    <div class="addon-header">
        <mat-icon class="addon-status-icon" [class.is-active]="this.access?.stateType === this.states.GRANTED">
            {{this.access?.stateType === this.states.GRANTED ? 'check_circle' : 'radio_button_unchecked'}}
        </mat-icon>
        <h2 class="addon-name">API Keys Management</h2>
        <span class="addon-badge" [class.is-active]="this.access?.stateType === this.states.GRANTED">
            <ng-container *ngIf="this.loading === this.loadingStates.LOADING">Loading</ng-container>
            <ng-container *ngIf="this.loading === this.loadingStates.ERROR">Unavailable</ng-container>
            <ng-container *ngIf="this.loading === this.loadingStates.LOADED"
                          [ngSwitch]="this.access.stateType">
                <ng-container *ngSwitchCase="this.states.NOT_REQUESTED">Not requested</ng-container>
                <ng-container *ngSwitchCase="this.states.PENDING">Pending</ng-container>
                <ng-container *ngSwitchCase="this.states.GRANTED">Active</ng-container>
                <ng-container *ngSwitchCase="this.states.WITHDRAWN">Revoked</ng-container>
                <ng-container *ngSwitchDefault>Unknown</ng-container>
            </ng-container>
        </span>
    </div>

    <div class="addon-body">
        <div *ngIf="this.loading === this.loadingStates.LOADING" class="addon-row">
            <mat-icon class="addon-row-icon">radio_button_unchecked</mat-icon>
            <div class="addon-row-text">
                <div class="addon-row-label">Loading API key access state ...</div>
            </div>
        </div>

        <div *ngIf="this.loading === this.loadingStates.ERROR" class="addon-row">
            <mat-icon class="addon-row-icon">radio_button_unchecked</mat-icon>
            <div class="addon-row-text">
                <div class="addon-row-label">Could not load API key access state.</div>
                <small class="addon-row-meta">Nothing about the account was changed.</small>
            </div>
            <button class="addon-row-action" color="primary" mat-raised-button (click)="this.load()">
                <mat-icon>refresh</mat-icon>
                Retry
            </button>
        </div>

        <ng-container *ngIf="this.loading === this.loadingStates.LOADED">
            <ng-container *ngIf="this.access.stateType !== undefined; else unrecognisedState">

                <!-- the status block: what the current state means, the key count as a figure, and the
                     one action slot. Nothing here is a milestone, so nothing here carries a tick that
                     could contradict the state beside it -->
                <div class="addon-row">
                    <div class="addon-row-text">
                        <div class="addon-row-label" [ngSwitch]="this.access.stateType">
                            <ng-container *ngSwitchCase="this.states.NOT_REQUESTED">The account has not requested API access.</ng-container>
                            <ng-container *ngSwitchCase="this.states.PENDING">Awaiting a decision.</ng-container>
                            <ng-container *ngSwitchCase="this.states.GRANTED">Access is active.</ng-container>
                            <ng-container *ngSwitchCase="this.states.WITHDRAWN">Access revoked. The account owner must request access again to regain it.</ng-container>
                            <ng-container *ngSwitchDefault>No action is available in this state.</ng-container>
                        </div>
                        <small class="addon-row-meta">Active API keys : {{this.access.activeKeyCount}}</small>
                        <!-- the outcome of the revocation just made in this session. It lives here and
                             not in the history on purpose: it is not on the record, so a re-read cannot
                             reproduce it, and it must be readable without opening anything. Its
                             condition is the count itself, never a date, so a withdraw response with no
                             withdrawnOn can still not hide how many keys were stopped -->
                        <small *ngIf="this.lastRevokedKeyCount !== undefined" class="addon-row-meta">
                            Stopped {{this.lastRevokedKeyCount}} active API key(s).
                        </small>
                    </div>
                    <!-- one action slot, and the two actions are mutually exclusive by state: PENDING
                         grants, GRANTED withdraws, every other state offers nothing at all -->
                    <button *ngIf="this.canDecide && !this.isOwnAccount && this.access.stateType === this.states.PENDING"
                            class="addon-row-action" color="primary" mat-raised-button
                            (click)="this.onGrantClick()"
                            [disabled]="this.actionInProgress">
                        <mat-icon>add_circle_outline</mat-icon>
                        Give access
                    </button>
                    <button *ngIf="this.canDecide && this.access.stateType === this.states.GRANTED"
                            class="addon-row-action" color="warn" mat-raised-button
                            (click)="this.onWithdrawClick()"
                            [disabled]="this.actionInProgress">
                        <mat-icon>remove_circle_outline</mat-icon>
                        Revoke access
                    </button>
                </div>

                <!-- the history: dated events, NEWEST FIRST by each event's own timestamp, and NO
                     state dependent icon anywhere in it. An event either happened and is listed, or it
                     is not listed at all, so a later event can never un-tick an earlier one. Not
                     rendered when there is nothing to open -->
                <div *ngIf="this.historyEventCount > 0" class="addon-row addon-history">
                    <mat-accordion class="addon-history-accordion">
                        <mat-expansion-panel class="addon-history-panel">
                            <mat-expansion-panel-header>
                                <mat-panel-title>Latest activity</mat-panel-title>
                                <mat-panel-description>{{this.historySummary}}</mat-panel-description>
                            </mat-expansion-panel-header>

                            <!-- newest first, ordered by each event's own timestamp: an event is shown
                                 whatever the state is now, because the question is 'did it happen', not
                                 'is it the state' -->
                            <div *ngFor="let event of this.historyEvents" class="addon-history-event">
                                <div class="addon-row-label">{{event.label}}</div>
                                <small class="addon-row-meta addon-history-when">{{event.date | date:'medium'}}<span *ngIf="event.actor"> · {{event.actor}}</span></small>
                            </div>

                            <!-- the record keeps ONE timestamp per event type, so an earlier request,
                                 approval or revocation is overwritten rather than kept. Saying so is the
                                 difference between a panel that is incomplete and one that lies -->
                            <small class="addon-row-meta addon-history-note">
                                Only the most recent request, approval and revocation are kept.
                            </small>
                        </mat-expansion-panel>
                    </mat-accordion>
                </div>

            </ng-container>
        </ng-container>

        <ng-template #unrecognisedState>
            <div class="addon-row">
                <mat-icon class="addon-row-icon">radio_button_unchecked</mat-icon>
                <div class="addon-row-text">
                    <div class="addon-row-label">This console does not recognise the current API key access state ({{this.access.state}}). No action is available.</div>
                </div>
            </div>
        </ng-template>
    </div>
</mat-card>
